/**
 * Admin Domains Management Page
 * Manage all client domain registrations and renewals
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  Search,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertTriangle,
  Lock,
  Unlock,
  XCircle,
  Calendar,
  DollarSign,
} from 'lucide-react';

interface Domain {
  id: number;
  clientId: number;
  clientName: string;
  clientEmail: string;
  domainName: string;
  status: 'active' | 'pending' | 'expired' | 'cancelled' | 'transferred';
  registrationDate: string;
  expiryDate: string;
  autoRenew: boolean;
  isLocked: boolean;
  recurringAmount: number;
  registrarId?: number;
  registrarName?: string;
}

const statusConfig = {
  active: {
    label: 'Active',
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
  },
  expired: {
    label: 'Expired',
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-900/20',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    color: 'text-slate-400',
    bgColor: 'bg-slate-900/20',
  },
  transferred: {
    label: 'Transferred',
    icon: RefreshCw,
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
  },
};

const DomainsPage: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadDomains();
  }, [filterStatus]);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const response = await fetch(`/api/v1/admin/domains?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setDomains(data.data.domains);
      }
    } catch (error) {
      console.error('Failed to load domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDomains = domains.filter((domain) => {
    const searchMatch =
      searchQuery === '' ||
      domain.domainName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      domain.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      domain.clientEmail.toLowerCase().includes(searchQuery.toLowerCase());

    return searchMatch;
  });

  const isExpiringSoon = (expiryDate: string) => {
    const daysUntilExpiry = Math.floor(
      (new Date(expiryDate).getTime() - new Date().getTime()) / 86400000
    );
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    return Math.floor((new Date(expiryDate).getTime() - new Date().getTime()) / 86400000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleToggleLock = async (domainId: number, currentLockStatus: boolean) => {
    try {
      await fetch(`/api/v1/admin/domains/${domainId}/${currentLockStatus ? 'unlock' : 'lock'}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadDomains();
    } catch (error) {
      console.error('Failed to toggle domain lock:', error);
      alert('Failed to update domain lock status');
    }
  };

  const handleToggleAutoRenew = async (domainId: number, currentAutoRenew: boolean) => {
    try {
      await fetch(`/api/v1/admin/domains/${domainId}/auto-renew`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ autoRenew: !currentAutoRenew }),
      });
      loadDomains();
    } catch (error) {
      console.error('Failed to toggle auto-renew:', error);
      alert('Failed to update auto-renew status');
    }
  };

  const handleDeleteDomain = async (domainId: number) => {
    if (
      !confirm(
        'Are you sure you want to delete this domain record? This will not cancel the domain at the registrar.'
      )
    )
      return;

    try {
      await fetch(`/api/v1/admin/domains/${domainId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      loadDomains();
    } catch (error) {
      console.error('Failed to delete domain:', error);
      alert('Failed to delete domain');
    }
  };

  const totalRevenue = domains
    .filter((d) => d.status === 'active')
    .reduce((sum, d) => sum + d.recurringAmount, 0);

  const expiringSoon = domains.filter(
    (d) => d.status === 'active' && isExpiringSoon(d.expiryDate)
  ).length;

  const autoRenewCount = domains.filter((d) => d.autoRenew && d.status === 'active').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Globe className="w-8 h-8" />
            Domain Management
          </h1>
          <p className="text-slate-400 mt-2">Manage client domain registrations and renewals</p>
        </div>
        <Link
          to="/admin/domains/register"
          className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2 font-medium"
        >
          <Plus className="w-5 h-5" />
          Register Domain
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Domains</p>
              <p className="text-2xl font-bold text-white mt-1">{domains.length}</p>
            </div>
            <Globe className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {domains.filter((d) => d.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Expiring Soon</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">{expiringSoon}</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-orange-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Auto-Renew</p>
              <p className="text-2xl font-bold text-purple-400 mt-1">{autoRenewCount}</p>
            </div>
            <RefreshCw className="w-10 h-10 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Annual Revenue</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {formatPrice(totalRevenue)}
              </p>
            </div>
            <DollarSign className="w-10 h-10 text-green-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by domain name, client name, or email..."
            className="w-full pl-10 pr-4 py-3 bg-slate-800/80 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {['all', 'active', 'pending', 'expired', 'cancelled', 'transferred'].map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-4 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                filterStatus === status
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Domains Table */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading domains...</span>
          </div>
        </div>
      ) : filteredDomains.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <Globe className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Domains Found</h3>
          <p className="text-slate-400">
            {searchQuery ? 'No domains match your search.' : 'No domains in the system yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Domain</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Client</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Registered</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Expires</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Renewal</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Lock</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredDomains.map((domain) => {
                  const statusInfo = statusConfig[domain.status];
                  const StatusIcon = statusInfo.icon;
                  const daysUntilExpiry = getDaysUntilExpiry(domain.expiryDate);
                  const expiringSoon = isExpiringSoon(domain.expiryDate);

                  return (
                    <tr key={domain.id} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <Link
                          to={`/admin/domains/${domain.id}`}
                          className="text-orange-400 hover:text-orange-300 font-medium"
                        >
                          {domain.domainName}
                        </Link>
                        {domain.registrarName && (
                          <div className="text-xs text-slate-400">{domain.registrarName}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <Link
                          to={`/admin/clients/${domain.clientId}`}
                          className="text-white hover:text-orange-400"
                        >
                          {domain.clientName}
                        </Link>
                        <div className="text-xs text-slate-400">{domain.clientEmail}</div>
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="p-4 text-slate-300">{formatDate(domain.registrationDate)}</td>
                      <td className="p-4">
                        <span
                          className={`${
                            expiringSoon ? 'text-orange-400 font-medium' : 'text-slate-300'
                          }`}
                        >
                          {formatDate(domain.expiryDate)}
                        </span>
                        {domain.status === 'active' && (
                          <div className="text-xs text-slate-400">
                            {daysUntilExpiry > 0 ? `${daysUntilExpiry} days` : 'Expired'}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleAutoRenew(domain.id, domain.autoRenew)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                            domain.autoRenew
                              ? 'bg-green-900/20 text-green-400'
                              : 'bg-slate-900/20 text-slate-400'
                          }`}
                        >
                          <RefreshCw className="w-3 h-3" />
                          {domain.autoRenew ? 'On' : 'Off'}
                        </button>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleLock(domain.id, domain.isLocked)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                            domain.isLocked
                              ? 'bg-red-900/20 text-red-400'
                              : 'bg-slate-900/20 text-slate-400'
                          }`}
                        >
                          {domain.isLocked ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Unlock className="w-3 h-3" />
                          )}
                          {domain.isLocked ? 'Locked' : 'Unlocked'}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/domains/${domain.id}`}
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="Manage"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleDeleteDomain(domain.id)}
                            className="p-2 text-red-400 hover:bg-slate-700/50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainsPage;
